import React from "react";
import PropTypes from "prop-types";

// PageContainer — the legacy page-root idiom, reused so TAT frames itself exactly
// like Orders / Quotations / Reorders / B2B do.
//
// `p-7 h-full overflow-y-scroll bg-gray-50` is copied verbatim from the legacy page
// roots. Copied, not converted: `src/index.scss` sets `font-size: 13px` on `*`, which
// also matches `html`, so 1rem = 13px app-wide and every hand-computed px equivalent
// of a Tailwind spacing class would be wrong. The class strings are the contract.
//
// This container used to cap content at `mx-auto w-full max-w-[1400px]`, with a
// `fullBleed` opt-out for wide tables. That cap solved a real measured problem — on a
// 1680px monitor, 1460px of unconstrained 13px table text has nothing to anchor the
// eye, and line length, not text size, was what made it hard to read. The cap is gone
// anyway, deliberately: legacy caps nothing, TAT sits one nav click from Orders, and
// a page that centres itself at a different width from the screen beside it reads as
// a different product. Visual consistency with the real PillEngine screens outranks
// the readability win here. If wide-table readability ever needs solving again, solve
// it the way legacy does — fewer columns — not with a container width v2 alone has.
//
// `fullBleed` is therefore gone from the signature too, not kept as a no-op: with the
// cap removed every page is full-bleed, and no caller ever passed it.
//
// The `h-full overflow-y-scroll` makes this an inner scroller nested inside OpsShell's
// `<main className="min-h-0 flex-1 overflow-y-auto">`. That double-scroller structure
// is legacy's own (`src/layouts/Dashboard/index.jsx`'s outer scroller wrapping a page
// root that scrolls again) and works for the same reason: the ancestor chain gives
// `<main>` a definite height, so `h-full` here resolves. Note `overflow-y-scroll`
// always reserves a scrollbar gutter, even on a short page — also true of legacy.

function PageContainer({ children, className }) {
    return (
        <div className={`p-7 h-full overflow-y-scroll bg-gray-50 ${className}`}>
            {children}
        </div>
    );
}

PageContainer.propTypes = {
    children: PropTypes.node,
    className: PropTypes.string,
};

PageContainer.defaultProps = {
    children: null,
    className: "",
};

export default PageContainer;
